/**
 * GSM-7 / UCS-2 SMS segment estimation for the notification template editor.
 * Mirrors how Telnyx bills: an SMS that fits the GSM-7 basic alphabet sends in
 * 160-char segments (153 when concatenated); a single non-GSM character (an
 * em-dash, a curly quote, an emoji) forces the whole message to UCS-2 at 70/67.
 * We keep our transactional copy to ONE GSM-7 segment, so the editor warns the
 * moment an edit pushes past that.
 */

// GSM 03.38 basic character set (each counts as 1 unit).
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1bÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
// GSM extension characters (each counts as 2 units - an escape + the char).
const GSM_EXTENDED = '^{}\\[~]|€';

const basicSet = new Set(GSM_BASIC.split(''));
const extSet = new Set(GSM_EXTENDED.split(''));

export type SmsEncoding = 'gsm' | 'ucs2';

export interface SmsEstimate {
  encoding: SmsEncoding;
  units: number;       // GSM units (ext chars = 2) or UCS-2 code units
  segments: number;
  perSegment: number;  // capacity of a single segment in this encoding
  /** Non-GSM characters found (so the editor can name the offenders). */
  badChars: string[];
}

// The usual culprits that silently force UCS-2 - surfaced by name in the UI.
const NAMED_BAD: Record<string, string> = {
  '—': 'em dash (—)',
  '–': 'en dash (–)',
  '‘': 'curly quote (‘)',
  '’': 'curly quote (’)',
  '“': 'curly quote (“)',
  '”': 'curly quote (”)',
  '…': 'ellipsis (…)',
};

export function estimateSms(text: string): SmsEstimate {
  let gsmOk = true;
  let units = 0;
  const bad: string[] = [];
  for (const ch of text) {
    if (basicSet.has(ch)) {
      units += 1;
    } else if (extSet.has(ch)) {
      units += 2;
    } else {
      gsmOk = false;
      const name = NAMED_BAD[ch] ?? `"${ch}"`;
      if (!bad.includes(name)) bad.push(name);
    }
  }

  if (gsmOk) {
    const perSegment = units > 160 ? 153 : 160;
    return { encoding: 'gsm', units, segments: Math.max(1, Math.ceil(units / perSegment)), perSegment: 160, badChars: [] };
  }
  // UCS-2: count code units (surrogate pairs = 2).
  const codeUnits = text.length;
  const perSegment = codeUnits > 70 ? 67 : 70;
  return {
    encoding: 'ucs2',
    units: codeUnits,
    segments: Math.max(1, Math.ceil(codeUnits / perSegment)),
    perSegment: 70,
    badChars: bad,
  };
}
